/**
 * Bodysocks delivery estimate + same-day-dispatch countdown (PDP).
 *
 * Two lines:
 *   1. Standard delivery, as a date range (dispatch + N–M working days).
 *   2. Faster delivery date + a live countdown to the next dispatch cut-off.
 *
 * Rules: dispatch is Mon–Fri. Order before the cut-off on a working day →
 * dispatched that day (fast delivery = the next working day). After the
 * cut-off / at weekends → dispatched the next working day, the fast date
 * becomes an actual date, and the countdown targets the cut-off on that next
 * dispatch day.
 *
 * Nothing here is UK-specific and nothing here is English: the store's time
 * zone, date locale, cut-off, transit times, the name of its faster service
 * and its free-delivery threshold arrive as data attributes from the block's
 * settings, and so does every shopper-facing string, already translated by
 * the block from the locale files. Word order is the locale string's
 * business — this file only substitutes its {{ placeholders }}.
 */

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Escape a value that is about to be concatenated into innerHTML. */
const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

/** Emphasised value: the <strong> is ours, the text inside it is escaped. */
const strong = (value) => `<strong>${escapeHtml(value)}</strong>`;

/**
 * Substitute a translated string's {{ placeholders }}. hasOwn, not `in`, so a
 * placeholder named after a prototype member resolves to nothing.
 */
const substitute = (template, values) =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (Object.hasOwn(values, name) ? values[name] : match));

/**
 * Fill a translated string that is going into innerHTML. The template is
 * escaped here and the values are escaped by the caller, so the only markup in
 * the result is the emphasis this file adds — no locale string, merchant
 * setting or formatted date reaches innerHTML raw.
 */
const fill = (template, values) => substitute(escapeHtml(template), values);

class DeliveryCountdown extends HTMLElement {
  /** @type {number | null} */
  #timer = null;

  connectedCallback() {
    this.standardEl = this.querySelector('[data-dc-standard]');
    this.cutoffEl = this.querySelector('[data-dc-cutoff]');
    this.cutoffHour = parseInt(this.dataset.cutoffHour || '13', 10);
    // A store that dispatches on the half hour sets this; 0 is a whole-hour cut-off.
    this.cutoffMinute = parseInt(this.dataset.cutoffMinute || '0', 10);
    this.timeZone = this.dataset.timeZone || 'Europe/London';
    this.locale = this.dataset.locale || 'en-GB';
    this.standardMinDays = parseInt(this.dataset.standardMinDays || '2', 10);
    this.standardMaxDays = parseInt(this.dataset.standardMaxDays || '3', 10);
    this.fastDays = parseInt(this.dataset.fastDays || '1', 10);
    // Blank on a store with no faster service — the whole segment is then left
    // out rather than showing a date with nothing to label it. No default here:
    // what this store calls that service is the block setting's to say.
    this.fastLabel = this.dataset.fastLabel || '';
    // Blank on a store with no free-delivery threshold — the clause is then
    // left out rather than quoting a figure that store never offers.
    this.freeOver = this.dataset.freeOver || '';
    // Shopper-facing copy, translated by the block from the locale files.
    this.strings = {
      cutoffSameDay: this.dataset.i18nCutoffSameDay || '',
      cutoffTomorrow: this.dataset.i18nCutoffTomorrow || '',
      cutoffDay: this.dataset.i18nCutoffDay || '',
      standard: this.dataset.i18nStandard || '',
      freeOver: this.dataset.i18nFreeOver || '',
      fast: this.dataset.i18nFast || '',
      timeMinutes: this.dataset.i18nTimeMinutes || '',
      timeHours: this.dataset.i18nTimeHours || '',
      timeDays: this.dataset.i18nTimeDays || '',
    };
    this.#update();
    this.#timer = window.setInterval(() => this.#update(), 60000);
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
  }

  /** Current store time as { y, mo (1-12), d, idx (0=Sun..6=Sat), hour, minute }. */
  #storeNow() {
    // en-GB here is a parsing locale, not a display one: the weekday lookup
    // below keys off English short names, so this must not follow this.locale.
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timeZone,
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    let hour = parseInt(get('hour'), 10);
    if (hour === 24) hour = 0;
    return {
      y: parseInt(get('year'), 10),
      mo: parseInt(get('month'), 10),
      d: parseInt(get('day'), 10),
      idx: { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[get('weekday')],
      hour,
      minute: parseInt(get('minute'), 10),
    };
  }

  /** Add n working days (skipping Sat/Sun) to a UTC-noon date. */
  #addWorkingDays(date, n) {
    const d = new Date(date.getTime());
    let added = 0;
    while (added < n) {
      d.setUTCDate(d.getUTCDate() + 1);
      const wd = d.getUTCDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return d;
  }

  #fmtWeekday(dt) {
    return new Intl.DateTimeFormat(this.locale, { timeZone: 'UTC', weekday: 'long' }).format(dt);
  }

  /**
   * Plain text — "2h 40m" in English, "2 Std. 40 Min." in German. Substituted
   * without escaping: the caller escapes the whole result once, when it wraps
   * it in the emphasis.
   */
  #fmtCountdown(mins) {
    if (mins < 60) return substitute(this.strings.timeMinutes, { minutes: mins });
    if (mins < 1440) {
      return substitute(this.strings.timeHours, { hours: Math.floor(mins / 60), minutes: mins % 60 });
    }
    return substitute(this.strings.timeDays, { days: Math.floor(mins / 1440), hours: Math.floor((mins % 1440) / 60) });
  }

  #update() {
    const now = this.#storeNow();
    if (now.idx == null || Number.isNaN(now.hour)) return;

    const base = new Date(Date.UTC(now.y, now.mo - 1, now.d, 12));
    const cutoffMin = this.cutoffHour * 60 + this.cutoffMinute;
    const nowMin = now.hour * 60 + now.minute;
    const isWorking = now.idx >= 1 && now.idx <= 5;

    let dispatch;
    let kDays; // calendar days from today to the dispatch day
    if (isWorking && nowMin < cutoffMin) {
      dispatch = base;
      kDays = 0;
    } else {
      dispatch = this.#addWorkingDays(base, 1);
      kDays = Math.round((dispatch.getTime() - base.getTime()) / 86400000);
    }

    const countdownMin = kDays * 1440 + (cutoffMin - nowMin);
    const fast = this.#addWorkingDays(dispatch, this.fastDays);
    const stdStart = this.#addWorkingDays(dispatch, this.standardMinDays);
    const stdEnd = this.#addWorkingDays(dispatch, this.standardMaxDays);

    // A whole sentence per case rather than a stitched-together fragment:
    // "same day", "tomorrow" and a weekday sit in different places in
    // different languages, so each case gets its own translatable string.
    const cutoffValues = { time: strong(this.#fmtCountdown(countdownMin)) };
    let cutoffTemplate;
    if (kDays === 0) {
      cutoffTemplate = this.strings.cutoffSameDay;
    } else if (kDays === 1) {
      cutoffTemplate = this.strings.cutoffTomorrow;
    } else {
      cutoffTemplate = this.strings.cutoffDay;
      cutoffValues.day = escapeHtml(this.#fmtWeekday(dispatch));
    }

    const monthShort = (dt) => new Intl.DateTimeFormat(this.locale, { timeZone: 'UTC', month: 'short' }).format(dt);
    const dayShort = (dt) =>
      new Intl.DateTimeFormat(this.locale, { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(dt);
    const rangeShort = (a, b) => {
      const ma = monthShort(a);
      const mb = monthShort(b);
      return ma === mb ? `${a.getUTCDate()}–${b.getUTCDate()} ${mb}` : `${a.getUTCDate()} ${ma}–${b.getUTCDate()} ${mb}`;
    };

    if (this.cutoffEl) {
      this.cutoffEl.innerHTML = `<span class="delivery-countdown__icon" aria-hidden="true">⏰</span> ${fill(cutoffTemplate, cutoffValues)}`;
    }
    if (this.standardEl) {
      let text = fill(this.strings.standard, { date: strong(rangeShort(stdStart, stdEnd)) });
      if (this.freeOver) {
        text += ` ${fill(this.strings.freeOver, { amount: escapeHtml(this.freeOver) })}`;
      }
      // No faster service on this store: the segment goes, separator and all.
      if (this.fastLabel) {
        text += ` · ${fill(this.strings.fast, { label: escapeHtml(this.fastLabel), date: strong(dayShort(fast)) })}`;
      }
      this.standardEl.innerHTML = `<span class="delivery-countdown__icon" aria-hidden="true">🚚</span> ${text}`;
    }
  }
}

if (!customElements.get('delivery-countdown')) {
  customElements.define('delivery-countdown', DeliveryCountdown);
}
